import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, APP_ID } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';
import type { AppUser, FirestoreUserData } from '../types';

// Path for users under artifacts (consistent with UserManagement)
const USERS_COLLECTION_PATH = `artifacts/${APP_ID}/public/data/users`;

interface UseAuthReturn {
  currentUser: AppUser | null;
  authLoading: boolean;
  handleLogout: () => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
  const toast = useToast();
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      if (user) {
        try {
          // Try to get user from artifacts path first (new location)
          let userDoc = await getDoc(doc(db, USERS_COLLECTION_PATH, user.uid));

          // Fallback to root users collection (legacy location)
          if (!userDoc.exists()) {
            userDoc = await getDoc(doc(db, 'users', user.uid));
          }

          if (userDoc.exists()) {
            const userData = userDoc.data() as FirestoreUserData;
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              role: userData.role,
              name: userData.name || user.displayName || user.email?.split('@')[0] || 'User',
              teamId: userData.teamId || null,
              playerId: userData.playerId || null,
            });
          } else {
            toast.error('Account not configured. Contact administrator.');
            await signOut(auth);
            setCurrentUser(null);
          }
        } catch (err) {
          console.error('Error fetching user role:', err);
          toast.error('Failed to load user profile.');
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }

      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [toast]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      toast.info('Logged out successfully');
    } catch (err) {
      toast.error('Failed to logout');
    }
  };

  return { currentUser, authLoading, handleLogout };
};

